import { useEffect, useState } from "react";
import { getAllUsers, getAllPlans } from "../../../shared/services/adminApi";

const Dashboard = () => {
    const [stats, setStats] = useState({
        totalUsers: 0,
        adminCount: 0,
        userCount: 0,
        totalPlans: 0,
    });
    const [recentUsers, setRecentUsers] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchDashboardData();
    }, []);

    const fetchDashboardData = async () => {
        setLoading(true);
        try {
            const [usersRes, plansRes] = await Promise.allSettled([
                getAllUsers(1, 100),
                getAllPlans(1, 100)
            ]);

            let totalUsers = 0;
            let adminCount = 0;
            let userCount = 0;
            let users = [];

            if (usersRes.status === "fulfilled") {
                const userData = usersRes.value?.data?.data || [];
                users = userData;
                totalUsers = userData.length;
                userData.forEach(user => {
                    const role = user?.role || user?.user_type || "";
                    if (role === "admin") {
                        adminCount++;
                    } else {
                        userCount++;
                    }
                });
            }

            let totalPlans = 0;
            if (plansRes.status === "fulfilled") {
                const planData = plansRes.value?.data?.data || [];
                totalPlans = planData.length;
            }

            setStats({ totalUsers, adminCount, userCount, totalPlans });
            setRecentUsers(users.slice(0, 5));
        } catch (error) {
            console.error("Error fetching dashboard data:", error);
        } finally {
            setLoading(false);
        }
    };

    const statCards = [
        { label: "Total Users", value: stats.totalUsers, icon: UsersIcon, colorClass: "stat-icon-blue" },
        { label: "Admins", value: stats.adminCount, icon: ShieldCheckIcon, colorClass: "stat-icon-purple" },
        { label: "Regular Users", value: stats.userCount, icon: UserGroupIcon, colorClass: "stat-icon-green" },
        { label: "Total Plans", value: stats.totalPlans, icon: PlanIcon, colorClass: "stat-icon-orange" },
    ];

    if (loading) {
        return (
            <div className="admin-loading">
                <div className="spinner"></div>
            </div>
        );
    }

    return (
        <div>
            {/* Stats Cards */}
            <div className="admin-stats-grid">
                {statCards.map((card, index) => (
                    <div className="admin-stat-card" key={`stat-${index}`}>
                        <div className={`stat-icon ${card.colorClass}`}>
                            <card.icon />
                        </div>
                        <div className="stat-content">
                            <div className="stat-label">{card.label}</div>
                            <div className="stat-value">{card.value}</div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Recent Users Table */}
            <div className="admin-table-container">
                <div className="admin-table-header">
                    <h4>Recent Users</h4>
                </div>
                {recentUsers.length === 0 ? (
                    <div className="admin-empty-state">
                        <div className="empty-icon">👥</div>
                        <p>No users found</p>
                    </div>
                ) : (
                    <table>
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Email</th>
                                <th>Mobile</th>
                                <th>Role</th>
                            </tr>
                        </thead>
                        <tbody>
                            {recentUsers.map((user, index) => (
                                <tr key={user._id || user.id || index}>
                                    <td>{user.first_name} {user.last_name}</td>
                                    <td>{user.email}</td>
                                    <td>{user.mobile || "- - -"}</td>
                                    <td>
                                        <span className={`role-badge role-${user.role || user.user_type || "user"}`}>
                                            {user.role || user.user_type || "user"}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};

export default Dashboard;

// Simple SVG Icons for admin dashboard
const UsersIcon = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
        <circle cx="9" cy="7" r="4"></circle>
        <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
        <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
    </svg>
);

const ShieldCheckIcon = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
        <polyline points="9 12 11 14 15 10"></polyline>
    </svg>
);

const UserGroupIcon = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
        <circle cx="9" cy="7" r="4"></circle>
        <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
        <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
    </svg>
);

const PlanIcon = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
        <line x1="16" y1="2" x2="16" y2="6"></line>
        <line x1="8" y1="2" x2="8" y2="6"></line>
        <line x1="3" y1="10" x2="21" y2="10"></line>
    </svg>
);